import React from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import { useStore } from "../../../helpers";
import { useTranslation } from "react-i18next";
import { FTPSearchBar } from "../../../components-new";
import { ModernPagination } from "./modern-pagination";
import { Select, MenuItem } from "@mui/material";

const PAGE_SIZES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

interface TaskSearchPanelProps {
    pageSize: number;
    pageNumber: number;
    totalPages: number;
    resultsSize: number;
    startIndex: number;
    endIndex: number;
    onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
    onPageSizeChange: (size: number) => void;
    onSearch: (query: string) => void;
    onFilterClick: () => void;
    onClearAll: () => void;
    hasActiveFilters: boolean;
}

export const TaskSearchPanel = observer(({
    pageSize,
    pageNumber,
    totalPages,
    resultsSize,
    startIndex,
    endIndex,
    onPageChange,
    onPageSizeChange,
    onSearch,
    onFilterClick,
    onClearAll,
    hasActiveFilters,
}: TaskSearchPanelProps) => {
    const { t } = useTranslation("task_manager");

    const handlePageSizeChange = (event: any) => {
        onPageSizeChange(event.target.value);
    };

    return (
        <>
            {/* Search and Filter Bar - Integrated Design */}
            <div className="mb-4">
                <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3">
                    {/* Search Bar */}
                    <div className="flex-1 max-w-md">
                        <FTPSearchBar
                            onSearch={onSearch}
                            placeHolder={t("search_tasks") || "Search tasks..."}
                            clientSideSearch={false}
                        />
                    </div>

                    {/* Filter and Clear Buttons - Desktop */}
                    <div className="hidden md:flex items-center gap-2">
                        {hasActiveFilters && (
                            <Button
                                variant="text"
                                startIcon={<ClearIcon />}
                                size="medium"
                                onClick={onClearAll}
                                sx={{
                                    color: "#EF4444",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    padding: "8px 16px",
                                    "&:hover": {
                                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                                        color: "#DC2626",
                                    },
                                }}
                            >
                                {t("clear_all") || "Clear All"}
                            </Button>
                        )}
                        <Button
                            variant="text"
                            startIcon={<FilterAltIcon />}
                            size="medium"
                            onClick={onFilterClick}
                            sx={{
                                color: "#6B7280",
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: "8px",
                                padding: "8px 16px",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    color: "#374151",
                                },
                            }}
                        >
                            {t("task_filters")}
                        </Button>
                    </div>

                    {/* Filter and Clear Buttons - Mobile */}
                    <div className="flex md:hidden items-center gap-2">
                        {hasActiveFilters && (
                            <Button
                                variant="text"
                                startIcon={<ClearIcon />}
                                size="small"
                                onClick={onClearAll}
                                sx={{
                                    color: "#EF4444",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    padding: "6px 12px",
                                    fontSize: "0.875rem",
                                    "&:hover": {
                                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                                        color: "#DC2626",
                                    },
                                }}
                            >
                                {t("clear_all") || "Clear"}
                            </Button>
                        )}
                        <Button
                            variant="text"
                            startIcon={<FilterAltIcon />}
                            size="small"
                            onClick={onFilterClick}
                            sx={{
                                color: "#6B7280",
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: "8px",
                                padding: "6px 12px",
                                fontSize: "0.875rem",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    color: "#374151",
                                },
                            }}
                        >
                            {t("task_filters") || "Filters"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Pagination and Results Info - Subtle Design */}
            <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-4 mb-4">
                {/* Results Count */}
                <p className="text-sm text-gray-500">
                    Showing <span className="font-medium text-gray-700">{startIndex}</span> to{" "}
                    <span className="font-medium text-gray-700">{endIndex}</span> of{" "}
                    <span className="font-medium text-gray-700">{resultsSize}</span> results
                </p>

                {/* Pagination Controls */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Rows:</span>
                        <Select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            size="small"
                            sx={{
                                minWidth: "70px",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                fontWeight: 500,
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E5E7EB",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#D1D5DB",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#3A80F4",
                                    borderWidth: "2px",
                                },
                            }}
                        >
                            {PAGE_SIZES.map((size) => (
                                <MenuItem
                                    value={size}
                                    key={size}
                                    sx={{
                                        fontWeight: 500,
                                        "&.Mui-selected": {
                                            backgroundColor: "#EFF6FF",
                                            color: "#3A80F4",
                                            "&:hover": {
                                                backgroundColor: "#DBEAFE",
                                            },
                                        },
                                    }}
                                >
                                    {size}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    <ModernPagination
                        handlePageChange={onPageChange}
                        pageNumber={pageNumber}
                        totalPages={totalPages}
                    />
                </div>
            </div>
        </>
    );
});

